import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import SearchInput from 'tio-ui/components/search-input';
import { gt, has, not } from 'tio-ui/utilities';
import {
  PhBookOpen,
  PhMagnifyingGlass,
  PhSparkle,
  PhX,
  PhChatCircleDots,
  PhGraduationCap,
  PhMoney,
  PhShieldCheck,
  PhBank,
  PhInfo,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';
import Callout from 'tio-ui/components/callout';
import GlossaryTermCard from 'tio-employee/components/reimagined/glossary-term-card';
import type {
  GlossaryTerm,
  GlossaryCategory,
} from 'tio-employee/components/reimagined/glossary-term-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';

interface CategoryFilter {
  id: GlossaryCategory;
  name: string;
  icon: unknown;
  count: number;
}

export default class ReimaginedGlossaryPage extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked searchQuery = '';
  @tracked selectedLetter: string | null = null;
  @tracked selectedCategory: GlossaryCategory | null = null;
  @tracked expandedTermId: string | null = null;

  alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  /**
   * Comprehensive glossary terms - would typically come from translations/API
   */
  get glossaryTerms(): GlossaryTerm[] {
    return [
      // A
      {
        id: 'avalanche',
        term: 'Avalanche Method',
        definition:
          'A debt repayment strategy where you pay off loans with the highest interest rate first while making minimum payments on others. This approach minimizes total interest paid over time.',
        category: 'repayment',
        relatedTerms: ['Snowball Method', 'Interest Rate'],
        examples: [
          'If you have loans at 7%, 5%, and 4% interest, you would focus extra payments on the 7% loan first.',
        ],
        tip: 'The avalanche method saves more money mathematically, but some people prefer the psychological wins of the snowball method.',
      },
      // D
      {
        id: 'default',
        term: 'Default',
        definition:
          'When a borrower fails to make payments on a student loan for an extended period (typically 270+ days for federal loans). Default has serious consequences including wage garnishment, tax refund seizure, and credit damage.',
        category: 'loans',
        relatedTerms: ['Delinquent', 'Loan Rehabilitation', 'Forbearance'],
        examples: [
          'Federal student loans enter default after 270 days of missed payments.',
          'Private loans may default after just 90 days of non-payment.',
        ],
        tip: 'If you are struggling to make payments, contact your servicer immediately. Options like income-driven repayment or forbearance can prevent default.',
      },
      {
        id: 'deferment',
        term: 'Deferment',
        definition:
          'A temporary postponement of loan payments granted by your loan servicer. During deferment on subsidized loans, interest does not accrue. On unsubsidized loans, interest continues to accumulate.',
        category: 'repayment',
        relatedTerms: ['Forbearance', 'Subsidized Loans', 'Grace Period'],
        relatedRoute: 'authenticated.questions-you-have.repayment-options',
        relatedRouteText: 'Explore repayment options',
        examples: [
          'A graduate school deferment allows you to postpone payments while pursuing an advanced degree.',
        ],
      },
      {
        id: 'delinquent',
        term: 'Delinquent',
        definition:
          'The status of a loan when a payment is late. A loan becomes delinquent the day after a missed payment and remains so until caught up or entering default.',
        category: 'loans',
        relatedTerms: ['Default', 'Grace Period'],
        tip: 'Even one day late is technically delinquent. Set up autopay to avoid accidental delinquency.',
      },
      // F
      {
        id: 'forbearance',
        term: 'Forbearance',
        definition:
          'A temporary reduction or suspension of loan payments due to financial hardship. Unlike deferment, interest typically accrues on all loan types during forbearance.',
        category: 'repayment',
        relatedTerms: ['Deferment', 'Income-Driven Repayment'],
        examples: [
          'General forbearance may be granted for up to 12 months at a time.',
          'Mandatory forbearance is required in certain situations, like medical residency.',
        ],
        tip: "Forbearance should be a last resort since interest capitalizes. Consider income-driven repayment if you'll have ongoing financial constraints.",
      },
      // G
      {
        id: 'grace-period',
        term: 'Grace Period',
        definition:
          'A set period after graduating, leaving school, or dropping below half-time enrollment before you must begin repaying your student loans. Most federal loans have a 6-month grace period.',
        category: 'loans',
        relatedTerms: ['Deferment', 'Repayment Plan'],
        examples: [
          'Direct Subsidized and Unsubsidized Loans have a 6-month grace period.',
          'Perkins Loans have a 9-month grace period.',
        ],
        tip: 'Consider making interest payments during your grace period to prevent capitalization, especially on unsubsidized loans.',
      },
      // I
      {
        id: 'ibr',
        term: 'Income-Based Repayment (IBR)',
        definition:
          'An income-driven repayment plan that caps monthly payments at 10-15% of discretionary income. Remaining balance is forgiven after 20-25 years of qualifying payments.',
        category: 'repayment',
        relatedTerms: ['PAYE', 'Income-Driven Repayment'],
        relatedRoute: 'authenticated.questions-you-have.repayment-plans.income-driven',
        relatedRouteText: 'Learn about IDR plans',
        examples: [
          'New borrowers as of July 1, 2014: 10% of discretionary income, 20-year forgiveness.',
          'Older borrowers: 15% of discretionary income, 25-year forgiveness.',
        ],
      },
      {
        id: 'idr',
        term: 'Income-Driven Repayment (IDR)',
        definition:
          'A category of federal student loan repayment plans that set monthly payments based on income and family size rather than loan balance. Includes PAYE, IBR, and ICR plans.',
        category: 'repayment',
        relatedTerms: ['IBR', 'PAYE', 'ICR'],
        relatedRoute: 'authenticated.questions-you-have.repayment-plans.income-driven',
        tip: 'IDR plans can qualify for PSLF. Recertify your income annually to keep payments accurate.',
      },
      {
        id: 'interest-capitalization',
        term: 'Interest Capitalization',
        definition:
          'When unpaid interest is added to the principal balance of a loan, causing you to pay interest on interest. This increases the total amount you owe.',
        category: 'financial',
        relatedTerms: ['Principal', 'Interest Rate', 'Forbearance'],
        examples: [
          'After a forbearance period, $1,000 of accrued interest might capitalize onto your $20,000 loan, making your new balance $21,000.',
        ],
        tip: 'Pay at least the interest during forbearance or deferment to prevent capitalization when possible.',
      },
      // L
      {
        id: 'loan-forgiveness',
        term: 'Loan Forgiveness',
        definition:
          'Programs that cancel all or part of your remaining student loan balance after meeting specific requirements, such as working in public service or making payments for a certain period.',
        category: 'forgiveness',
        relatedTerms: ['PSLF', 'Teacher Loan Forgiveness', 'IDR Forgiveness'],
        relatedRoute: 'authenticated.questions-you-have.loan-forgiveness',
        relatedRouteText: 'Explore forgiveness options',
      },
      {
        id: 'loan-servicer',
        term: 'Loan Servicer',
        definition:
          'A company that handles the billing and other services on your student loans. They process payments, respond to customer inquiries, and manage repayment plans. Your servicer is assigned by the Department of Education.',
        category: 'loans',
        relatedTerms: ['Federal Student Aid', 'MOHELA'],
        examples: ['Major servicers include MOHELA, Nelnet, EdFinancial, and Aidvantage.'],
        tip: 'Find your servicer at StudentAid.gov. Keep their contact info handy for any loan questions.',
      },
      // P
      {
        id: 'paye',
        term: 'Pay As You Earn (PAYE)',
        definition:
          'An income-driven repayment plan capping payments at 10% of discretionary income with forgiveness after 20 years. Only available to new borrowers as of October 1, 2007.',
        category: 'repayment',
        relatedTerms: ['IBR', 'IDR'],
        relatedRoute: 'authenticated.questions-you-have.repayment-plans.income-driven',
      },
      {
        id: 'principal',
        term: 'Principal',
        definition:
          'The original amount borrowed, not including interest or fees. When you make payments, a portion goes to principal and a portion to interest.',
        category: 'financial',
        relatedTerms: ['Interest Rate', 'Amortization'],
        examples: [
          'If you borrowed $30,000 and have paid down $5,000 in principal, your principal balance is $25,000.',
        ],
      },
      {
        id: 'pslf',
        term: 'Public Service Loan Forgiveness (PSLF)',
        definition:
          'A federal program that forgives the remaining balance on Direct Loans after 120 qualifying monthly payments while working full-time for a qualifying employer (government or nonprofit).',
        category: 'forgiveness',
        relatedTerms: ['Qualifying Employer', 'IDR', 'Direct Loans'],
        relatedRoute: 'authenticated.pslf.dashboard.index',
        relatedRouteText: 'Track your PSLF progress',
        tip: 'Submit your Employment Certification Form annually to track your progress and catch any issues early.',
      },
      // R
      {
        id: 'refinancing',
        term: 'Refinancing',
        definition:
          'Replacing one or more existing loans with a new private loan, typically to get a lower interest rate or different terms. Important: Refinancing federal loans into private loans means losing federal benefits like IDR and forgiveness.',
        category: 'repayment',
        relatedTerms: ['Consolidation', 'Interest Rate', 'Private Loans'],
        relatedRoute: 'authenticated.repaying-student-debt.repayment-options',
        examples: [
          'If you have good credit and stable income, you might refinance from 6.5% to 4% interest.',
        ],
        tip: 'Never refinance federal loans if you might need income-driven repayment or are pursuing PSLF.',
      },
      // S
      {
        id: 'slr',
        term: 'Student Loan Repayment (SLR) Benefit',
        definition:
          'An employer-sponsored benefit where your company makes payments toward your student loans. Contributions may be tax-free up to $5,250 per year under Section 127.',
        category: 'financial',
        relatedTerms: ['Section 127', 'Employer Contributions'],
        relatedRoute: 'authenticated.slr.dashboard',
        relatedRouteText: 'View your SLR benefit',
        tip: "If your employer offers SLR, you're essentially getting a raise that goes directly to your loans!",
      },
      {
        id: 'snowball',
        term: 'Snowball Method',
        definition:
          'A debt repayment strategy where you pay off the smallest balance first while making minimum payments on others. This creates psychological wins that motivate continued progress.',
        category: 'repayment',
        relatedTerms: ['Avalanche Method', 'Debt Strategy'],
        examples: [
          'With debts of $2,000, $5,000, and $10,000, you would focus on the $2,000 first regardless of interest rates.',
        ],
      },
      {
        id: 'subsidized',
        term: 'Subsidized Loans',
        definition:
          'Federal student loans for undergraduates with demonstrated financial need. The government pays the interest while you are in school at least half-time, during grace periods, and during deferment.',
        category: 'loans',
        relatedTerms: ['Unsubsidized Loans', 'Direct Loans', 'Financial Need'],
        examples: ['Direct Subsidized Loans have annual and aggregate borrowing limits.'],
      },
      // T
      {
        id: 'teacher-forgiveness',
        term: 'Teacher Loan Forgiveness',
        definition:
          'A federal program that forgives up to $17,500 of Direct or FFEL Loans for teachers who work full-time for 5 consecutive years in a low-income school.',
        category: 'forgiveness',
        relatedTerms: ['PSLF', 'Qualifying Employment'],
        relatedRoute: 'authenticated.questions-you-have.loan-forgiveness.teacher',
        relatedRouteText: 'Check your eligibility',
        tip: "You can't receive Teacher Loan Forgiveness and PSLF credit for the same period of teaching service.",
      },
      // U
      {
        id: 'unsubsidized',
        term: 'Unsubsidized Loans',
        definition:
          "Federal student loans available to undergraduate and graduate students regardless of financial need. Interest accrues from the date of disbursement, including while you're in school.",
        category: 'loans',
        relatedTerms: ['Subsidized Loans', 'Direct Loans', 'Interest Capitalization'],
        tip: 'Consider paying interest on unsubsidized loans while in school to prevent your balance from growing.',
      },
    ];
  }

  @cached
  get categories(): CategoryFilter[] {
    const counts = this.glossaryTerms.reduce(
      (acc, term) => {
        acc[term.category] = (acc[term.category] || 0) + 1;
        return acc;
      },
      {} as Record<GlossaryCategory, number>
    );

    return [
      { id: 'loans', name: 'Loans', icon: PhGraduationCap, count: counts.loans || 0 },
      { id: 'repayment', name: 'Repayment', icon: PhMoney, count: counts.repayment || 0 },
      {
        id: 'forgiveness',
        name: 'Forgiveness',
        icon: PhShieldCheck,
        count: counts.forgiveness || 0,
      },
      { id: 'financial', name: 'Financial', icon: PhBank, count: counts.financial || 0 },
      { id: 'general', name: 'General', icon: PhInfo, count: counts.general || 0 },
    ];
  }

  @cached
  get availableLetters() {
    const letters = new Set<string>();
    this.glossaryTerms.forEach((term) => {
      const firstLetter = term.term[0]?.toUpperCase();
      if (firstLetter && /[A-Z]/.test(firstLetter)) {
        letters.add(firstLetter);
      }
    });
    return letters;
  }

  @cached
  get filteredTerms() {
    let terms = [...this.glossaryTerms];

    // Filter by letter
    if (this.selectedLetter) {
      terms = terms.filter((term) => term.term[0]?.toUpperCase() === this.selectedLetter);
    }

    // Filter by category
    if (this.selectedCategory) {
      terms = terms.filter((term) => term.category === this.selectedCategory);
    }

    // Filter by search
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      terms = terms.filter(
        (term) =>
          term.term.toLowerCase().includes(query) ||
          term.definition.toLowerCase().includes(query) ||
          term.relatedTerms?.some((rt) => rt.toLowerCase().includes(query))
      );
    }

    // Sort alphabetically
    return terms.sort((a, b) => a.term.localeCompare(b.term));
  }

  @cached
  get activeFiltersCount() {
    let count = 0;
    if (this.selectedLetter) count++;
    if (this.selectedCategory) count++;
    if (this.searchQuery) count++;
    return count;
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get glossaryDataSource() {
    // Glossary terms are static content
    return 'live' as const;
  }

  setSearch = (value: string) => {
    this.searchQuery = value;
  };

  selectLetter = (letter: string | null) => {
    this.selectedLetter = this.selectedLetter === letter ? null : letter;
  };

  selectCategory = (category: GlossaryCategory | null) => {
    this.selectedCategory = this.selectedCategory === category ? null : category;
  };

  clearFilters = () => {
    this.selectedLetter = null;
    this.selectedCategory = null;
    this.searchQuery = '';
  };

  toggleTerm = (termId: string, expanded: boolean) => {
    this.expandedTermId = expanded ? termId : null;
  };

  askAIAboutTerm = (_term: GlossaryTerm) => {
    // Would open AI chat with the term as context
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack @collapsed={{true}}>
      {{! Hero Section }}
      <PageHero
        @icon={{PhBookOpen}}
        @title="Financial Glossary"
        @subtitle="Understand the terms that matter for your student loan journey"
        @gradient="cool"
        role="banner"
      >
        <:content>
          <div class="tio-row-loose mt-4">
            <Chip class="tio-growth-glossary-stat">
              {{this.glossaryTerms.length}}
              Terms
            </Chip>
            <Chip class="tio-growth-glossary-stat">
              {{this.categories.length}}
              Categories
            </Chip>
          </div>
        </:content>
      </PageHero>

      {{! Search & Filters Bar }}
      <div class="sticky top-0 z-10 tio-bg-surface border-b tio-border-default shadow-sm">
        <div class="px-6 py-4">
          <div class="tio-col-loose md:flex-row">
            {{! Search }}
            <div class="flex-1">
              <SearchInput
                @value={{this.searchQuery}}
                @onChange={{this.setSearch}}
                @onSearch={{this.setSearch}}
                @placeholder="Search terms, definitions, or related topics..."
                @label="Search glossary"
                class="w-full"
              />
            </div>

            {{! Category Filter }}
            <div class="tio-row-tight overflow-x-auto pb-2 md:pb-0">
              <Chip
                class="tio-growth-glossary-category
                  {{unless this.selectedCategory 'tio-growth-glossary-category--active'}}"
                {{on "click" (fn this.selectCategory null)}}
              >
                All
              </Chip>
              {{#each this.categories as |category|}}
                {{#let category.icon as |CategoryIcon|}}
                  <Chip
                    class="tio-growth-glossary-category
                      {{if
                        (eq this.selectedCategory category.id)
                        'tio-growth-glossary-category--active'
                      }}"
                    {{on "click" (fn this.selectCategory category.id)}}
                  >
                    {{! @glint-expect-error: dynamic icon }}
                    <CategoryIcon @size={{14}} class="mr-1" />
                    {{category.name}}
                    <span class="ml-1 text-xs opacity-70">({{category.count}})</span>
                  </Chip>
                {{/let}}
              {{/each}}
            </div>
          </div>

          {{! Alphabet Filter }}
          <div class="tio-flex-center flex-wrap gap-1 mt-4">
            {{#each this.alphabet as |letter|}}
              {{#let (has this.availableLetters letter) as |isAvailable|}}
                <button
                  type="button"
                  disabled={{not isAvailable}}
                  class="tio-growth-glossary-letter"
                  data-available={{if isAvailable "true" "false"}}
                  data-selected={{if (eq this.selectedLetter letter) "true" "false"}}
                  {{on "click" (fn this.selectLetter letter)}}
                >
                  {{letter}}
                </button>
              {{/let}}
            {{/each}}
          </div>

          {{! Active Filters Indicator }}
          {{#if (gt this.activeFiltersCount 0)}}
            <div class="tio-row-between mt-3 pt-3 border-t tio-border-subtle">
              <Text @size="sm" class="tio-fg-secondary">
                Showing
                {{this.filteredTerms.length}}
                of
                {{this.glossaryTerms.length}}
                terms
              </Text>
              <Button
                @intent="primary"
                @appearance="minimal"
                class="text-sm"
                {{on "click" this.clearFilters}}
              >
                <PhX @size={{14}} class="mr-1" />
                Clear Filters
              </Button>
            </div>
          {{/if}}
        </div>
      </div>

      {{! AI Helper Banner }}
      <Callout
        @intent="info"
        @gradient={{true}}
        @iconBold={{true}}
        @align="center"
        @title="Can't find what you're looking for?"
        class="mx-6 mt-6"
      >
        <:icon><PhSparkle @size={{24}} /></:icon>
        <:default>
          <p class="text-sm">
            Ask our AI advisor to explain any student loan term in plain language.
          </p>
        </:default>
        <:action>
          <Button @intent="primary" @appearance="outlined" class="shrink-0">
            <PhChatCircleDots @size={{18}} class="mr-2" />
            Ask AI
          </Button>
        </:action>
      </Callout>

      {{! Terms List }}
      <div class="px-6 py-8">
        <DataIndicator
          @source={{this.glossaryDataSource}}
          @dataSourceId="glossary-terms"
          @label="Glossary Terms"
        >
          {{#if (gt this.filteredTerms.length 0)}}
            <div class="space-y-4 max-w-4xl">
              {{#each this.filteredTerms as |term|}}
                <GlossaryTermCard
                  @term={{term}}
                  @expanded={{eq this.expandedTermId term.id}}
                  @onToggle={{this.toggleTerm}}
                  @onAskAI={{this.askAIAboutTerm}}
                />
              {{/each}}
            </div>
          {{else}}
            <div class="text-center py-16">
              <PhMagnifyingGlass @size={{48}} class="mx-auto tio-fg-disabled mb-4" />
              <Heading @level={{3}} class="tio-fg-secondary mb-2">
                No terms found
              </Heading>
              <Text class="tio-fg-muted mb-4">
                Try adjusting your search or filters
              </Text>
              <Button @intent="primary" @appearance="outlined" {{on "click" this.clearFilters}}>
                Clear All Filters
              </Button>
            </div>
          {{/if}}
        </DataIndicator>
      </div>

      {{! Footer CTA }}
      <div class="tio-growth-glossary-coach-cta tio-gradient-info">
        <div class="tio-stack-center md:flex-row gap-6">
          <div class="flex-1 text-center md:text-left">
            <Heading @level={{3}} class="tio-fg-inverse mb-2">
              Still Have Questions?
            </Heading>
            <Text class="tio-growth-glossary-coach-copy">
              Our expert loan coaches can explain these concepts in the context of your specific
              situation and help you make informed decisions.
            </Text>
          </div>
          <Button
            @intent="primary"
            class="tio-bg-surface tio-fg-info hover:tio-bg-info-soft-hover shrink-0"
          >
            Talk to a Coach
          </Button>
        </div>
      </div>
    </VStack>
  </template>
}
